import {useCallback, useEffect} from 'react';
import {Platform} from 'react-native';

import {getAdminConversationDoctors} from '../../server/react-query/useAdminChat';
import {getAdminDashboardReport} from '../../server/react-query/useAdminDashboard';
import {registerAdminPwaPushAfterUserAction} from '../../utils/admin-pwa-push.utils';
import {ADMIN_PWA_BADGE_REFRESH_EVENT} from '../../utils/admin-pwa-badge-event.utils';
import {setWebAppBadge} from '../../utils/web-app-badge.utils';

const REFRESH_INTERVAL_MS = 30_000;

const useAdminPwaBadge = () => {
  const refreshBadge = useCallback(async () => {
    try {
      const [dashboard, conversations] = await Promise.all([
        getAdminDashboardReport(),
        getAdminConversationDoctors(),
      ]);

      const pendingVerificationCount = Number(
        dashboard?.practitionerCount?.pending,
      ) || 0;
      const unreadDoctorMessageCount = Array.isArray(conversations)
        ? conversations.reduce(
            (total, conversation) =>
              total + (Number(conversation?.unreadMessageCount) || 0),
            0,
          )
        : 0;

      await setWebAppBadge(
        pendingVerificationCount + unreadDoctorMessageCount,
      );
    } catch (error) {
      // Preserve the last known badge during temporary API/network failures.
      console.warn('Unable to refresh the admin PWA badge:', error);
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return undefined;
    }

    refreshBadge();
    const interval = window.setInterval(refreshBadge, REFRESH_INTERVAL_MS);

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshBadge();
      }
    };
    const refreshFromServiceWorker = (event: MessageEvent) => {
      if (event.data === 'Refresh admin badge') {
        refreshBadge();
      }
    };

    window.addEventListener('focus', refreshBadge);
    window.addEventListener(ADMIN_PWA_BADGE_REFRESH_EVENT, refreshBadge);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    navigator.serviceWorker?.addEventListener(
      'message',
      refreshFromServiceWorker,
    );

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshBadge);
      window.removeEventListener(ADMIN_PWA_BADGE_REFRESH_EVENT, refreshBadge);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      navigator.serviceWorker?.removeEventListener(
        'message',
        refreshFromServiceWorker,
      );
    };
  }, [refreshBadge]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return undefined;
    }

    return registerAdminPwaPushAfterUserAction();
  }, []);

  return refreshBadge;
};

export default useAdminPwaBadge;
